<template>
  <div>
    <v-container fluid>
      <!-- Combined Header Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h5 font-weight-bold primary--text mb-1">
                    Delete Account Requests
                  </h2>
                  <p class="text-body-2 grey--text ma-0">
                    Manage account deletion requests
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="error"
                      elevation="2"
                      rounded
                      to="/delete-requests/create"
                      class="px-5"
                    >
                      <v-icon left small>mdi-delete</v-icon>
                      Request Account Deletion
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <v-card-title class="pb-0">
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search requests..."
                single-line
                hide-details
                outlined
                dense
                class="mt-4"
                style="max-width: 300px"
              ></v-text-field>
            </v-card-title>
            
            <v-data-table
              :headers="headers"
              :items="deleteRequests"
              :search="search"
              :loading="loading"
              loading-text="Loading delete requests..."
              no-data-text="No delete requests found"
              class="px-4 pb-4"
            >
              <template #[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  text-color="white"
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template #[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <template #[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="viewRequest(item)"
                      v-on="on"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View Details</span>
                </v-tooltip>
                
                <v-tooltip bottom v-if="item.status === 'pending'">
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      color="success"
                      @click="approveRequest(item)"
                      v-on="on"
                    >
                      <v-icon small>mdi-check</v-icon>
                    </v-btn>
                  </template>
                  <span>Approve Request</span>
                </v-tooltip>

                <v-tooltip bottom v-if="item.status === 'pending'">
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="rejectRequest(item)"
                      v-on="on"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <span>Reject Request</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- View Request Dialog -->
      <v-dialog v-model="viewDialog" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="text-h6">Delete Request Details</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="viewDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text v-if="selectedRequest">
            <v-row>
              <v-col cols="12">
                <strong>User:</strong> {{ selectedRequest.user_name }}
              </v-col>
              <v-col cols="12">
                <strong>Email:</strong> {{ selectedRequest.user_email }}
              </v-col>
              <v-col cols="12">
                <strong>Status:</strong>
                <v-chip
                  small
                  :color="getStatusColor(selectedRequest.status)"
                  text-color="white"
                  class="ml-2"
                >
                  {{ selectedRequest.status }}
                </v-chip>
              </v-col>
              <v-col cols="12">
                <strong>Reason:</strong>
                <p class="mt-2">{{ selectedRequest.reason }}</p>
              </v-col>
              <v-col cols="12">
                <strong>Request Date:</strong> {{ formatDate(selectedRequest.created_at) }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "DeleteRequestsIndex",
  data() {
    return {
      loading: false,
      search: "",
      deleteRequests: [],
      viewDialog: false,
      selectedRequest: null,
      headers: [
        { text: "ID", value: "id", sortable: true },
        { text: "User", value: "user_name", sortable: true },
        { text: "Email", value: "user_email", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Request Date", value: "created_at", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    ...mapState(["errors"]),
  },
  async mounted() {
    await this.fetchDeleteRequests();
  },
  methods: {
    async fetchDeleteRequests() {
      this.loading = true;
      try {
        const response = await this.$http.get("/delete-requests");
        this.deleteRequests = response.data.data || [];
      } catch (error) {
        console.error("Error fetching delete requests:", error);
        this.$store.dispatch("setError", "Failed to fetch delete requests");
      } finally {
        this.loading = false;
      }
    },
    
    viewRequest(item) {
      this.selectedRequest = item;
      this.viewDialog = true;
    },
    
    async approveRequest(item) {
      if (confirm("Are you sure you want to approve this deletion request? This action cannot be undone.")) {
        try {
          const response = await this.$http.patch(`/delete-requests/${item.id}/approve`);
          if (response.data.success) {
            this.$store.dispatch("setSuccess", "Request approved successfully");
            await this.fetchDeleteRequests();
          }
        } catch (error) {
          this.$store.dispatch("setError", "Failed to approve request");
        }
      }
    },
    
    async rejectRequest(item) {
      if (confirm("Are you sure you want to reject this deletion request?")) {
        try {
          const response = await this.$http.patch(`/delete-requests/${item.id}/reject`);
          if (response.data.success) {
            this.$store.dispatch("setSuccess", "Request rejected successfully");
            await this.fetchDeleteRequests();
          }
        } catch (error) {
          this.$store.dispatch("setError", "Failed to reject request");
        }
      }
    },
    
    getStatusColor(status) {
      switch (status) {
        case "pending":
          return "orange";
        case "approved":
          return "success";
        case "rejected":
          return "error";
        case "completed":
          return "blue";
        default:
          return "grey";
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },
};
</script>